import { Injectable, inject } from '@angular/core';
import { collection, query, where, orderBy } from 'firebase/firestore';
import { Observable, of, map, switchMap } from 'rxjs';
import { FIREBASE_FIRESTORE } from '../firebase-config';
import { Region } from '../interfaces/region';
import { Faq } from '../interfaces/faq';
import { Version } from '../interfaces/version';
import { Terms } from '../interfaces/terms';
import { Privacy } from '../interfaces/privacy';
import { Provision } from '../interfaces/provision';
import { collectionDataObservable, docDataObservable } from './firestore-helpers';
import { catchPermissionDenied } from '../utils/firestore-error.utils';

interface IndexedRecord {
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class ContentDataService {
  private firestore = inject(FIREBASE_FIRESTORE);

  private readonly emptyRegions: Region[] = [];
  private readonly emptyFaqs: Faq[] = [];
  private readonly emptyVersions: Version[] = [];
  private readonly emptyTerms: Terms[] = [];
  private readonly emptyPrivacy: Privacy[] = [];
  private readonly emptyProvisions: Provision[] = [];

  getActiveRegions(): Observable<Region[]> {
    return collectionDataObservable<Region>(this.firestore, (fs) => {
      const regionsCollection = collection(fs, 'regions');
      return query(regionsCollection, where('is_active', '==', true), orderBy('title'));
    }, { idField: 'id' }).pipe(
      map((regions) => regions.map((region) => this.normalizeRegion(region))),
      map((regions) => [...regions].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''))),
      catchPermissionDenied(this.emptyRegions)
    );
  }

  getRegion(regionId: string): Observable<Region | undefined> {
    return docDataObservable<Region>(this.firestore, `regions/${regionId}`, { idField: 'id' }).pipe(
      catchPermissionDenied<Region | undefined>(undefined)
    );
  }

  getFaqList(key: string): Observable<Faq[]> {
    return collectionDataObservable<Faq>(this.firestore, (fs) => {
      const faqsCollection = collection(fs, 'faqs');
      return query(faqsCollection, where('is_active', '==', true));
    }, { idField: 'id' }).pipe(
      map((faqs) => {
        const sortFaqs = (items: Faq[]): Faq[] => [...items].sort((a, b) => {
          const aOrder = Number((a as { position?: unknown }).position ?? a.order ?? Number.MAX_SAFE_INTEGER);
          const bOrder = Number((b as { position?: unknown }).position ?? b.order ?? Number.MAX_SAFE_INTEGER);
          return aOrder - bOrder;
        });

        const normalizeKey = (value: unknown): string =>
          String(value ?? '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/_+/g, '-');

        const requestedKey = normalizeKey(key);
        const keyVariants = new Set<string>([
          requestedKey,
          requestedKey.replace(/faqs/g, 'faq'),
          requestedKey.replace(/faq$/g, 'faqs'),
        ]);

        const matchesRequestedKey = (faq: Faq): boolean => {
          const faqKey = normalizeKey(faq.key);
          return faqKey !== '' && keyVariants.has(faqKey);
        };

        const filteredFaqs = faqs.filter(matchesRequestedKey);
        return sortFaqs(filteredFaqs);
      }),
      catchPermissionDenied(this.emptyFaqs)
    );
  }

  getFaqDetails(faqId: string): Observable<Faq | undefined> {
    return docDataObservable<Faq>(this.firestore, `faqs/${faqId}`, { idField: 'id' }).pipe(
      catchPermissionDenied<Faq | undefined>(undefined)
    );
  }

  getSetting(key: string): Observable<IndexedRecord | undefined> {
    return docDataObservable<IndexedRecord>(this.firestore, `settings/${key}`, { idField: 'id' }).pipe(
      catchPermissionDenied<IndexedRecord | undefined>(undefined)
    );
  }

  getBooleanSetting(key: string, defaultValue = false): Observable<boolean> {
    return this.getSetting(key).pipe(
      map((setting) => {
        if (typeof setting !== 'object' || setting === null) {
          return defaultValue;
        }

        const value = Reflect.get(setting, 'value');
        return typeof value === 'boolean' ? value : defaultValue;
      })
    );
  }

  getMaintenanceMode(): Observable<boolean> {
    return of(true).pipe(
      switchMap(() => this.getSetting('maintenance')),
      switchMap((maintenanceSetting) =>
        of([maintenanceSetting, this.getSetting('maintenace'), this.getSetting('settings')]).pipe(
          switchMap(([legacyMaintenanceSetting, groupedSettings]) => {
            const combined = [maintenanceSetting, legacyMaintenanceSetting, groupedSettings];
            return of(combined.some((setting) => this.readMaintenanceToggle(setting)));
          })
        )
      )
    );
  }

  getActiveVersion(): Observable<Version[]> {
    return collectionDataObservable<Version>(this.firestore, (fs) => query(collection(fs, 'settings/versions/versions')), { idField: 'id' }).pipe(
      map((versions) => {
        const active = versions.filter((version) => this.isDocActive(version as IndexedRecord));
        return [...active].sort((a, b) => {
          const aNumber = String(a.number ?? '');
          const bNumber = String(b.number ?? '');
          return bNumber.localeCompare(aNumber, undefined, { numeric: true, sensitivity: 'base' });
        });
      }),
      catchPermissionDenied(this.emptyVersions)
    );
  }

  getActiveTerms(): Observable<Terms[]> {
    return collectionDataObservable<Terms>(this.firestore, (fs) => query(collection(fs, 'settings/agreements/terms')), { idField: 'id' }).pipe(
      map((terms) => {
        const active = terms.filter((term) => this.isDocActive(term as IndexedRecord));
        return [...active].sort((a, b) => {
          const aVersion = String(a.version ?? '');
          const bVersion = String(b.version ?? '');
          return bVersion.localeCompare(aVersion, undefined, { numeric: true, sensitivity: 'base' });
        });
      }),
      catchPermissionDenied(this.emptyTerms)
    );
  }

  getActivePrivacy(): Observable<Privacy[]> {
    return collectionDataObservable<Privacy>(this.firestore, (fs) => query(collection(fs, 'settings/agreements/privacy')), { idField: 'id' }).pipe(
      map((privacyDocs) => {
        const active = privacyDocs.filter((privacy) => this.isDocActive(privacy as IndexedRecord));
        return [...active].sort((a, b) => {
          const aVersion = String(a.version ?? '');
          const bVersion = String(b.version ?? '');
          return bVersion.localeCompare(aVersion, undefined, { numeric: true, sensitivity: 'base' });
        });
      }),
      catchPermissionDenied(this.emptyPrivacy)
    );
  }

  getActiveProvisions(): Observable<Provision[]> {
    return collectionDataObservable<Provision>(this.firestore, (fs) => query(collection(fs, 'settings/agreements/provision')), { idField: 'id' }).pipe(
      map((provisions) => {
        const active = provisions.filter((provision) => this.isDocActive(provision as IndexedRecord));
        return [...active].sort((a, b) => {
          const aVersion = String(a.version ?? '');
          const bVersion = String(b.version ?? '');
          return bVersion.localeCompare(aVersion, undefined, { numeric: true, sensitivity: 'base' });
        });
      }),
      catchPermissionDenied(this.emptyProvisions)
    );
  }

  private normalizeRegion(region: Region): Region {
    const source = region as Region & {
      name?: unknown;
      active?: unknown;
      enabled?: unknown;
    };

    const title = typeof source.title === 'string' && source.title.trim() !== ''
      ? source.title
      : typeof source.name === 'string'
        ? source.name
        : '';

    const isActive = typeof source.is_active === 'boolean'
      ? source.is_active
      : typeof source.active === 'boolean'
        ? source.active
        : typeof source.enabled === 'boolean'
          ? source.enabled
          : true;

    return {
      ...region,
      title,
      is_active: isActive,
    };
  }

  private isDocActive(source: IndexedRecord | null | undefined): boolean {
    if (!source) {
      return false;
    }

    const candidates = [
      source['is_active'],
      source['isActive'],
      source['active'],
      source['enabled'],
      source['status'],
    ];

    const hasExplicitActiveField = candidates.some((value) => value !== undefined && value !== null && value !== '');
    if (!hasExplicitActiveField) {
      return true;
    }

    return candidates.some((value) => {
      if (value === true || value === 1 || value === '1') {
        return true;
      }

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return normalized === 'true' || normalized === 'active' || normalized === 'enabled' || normalized === 'yes';
      }

      return false;
    });
  }

  private readMaintenanceToggle(source: unknown): boolean {
    const fields = ['value', 'enabled', 'maintenance', 'maintenace'];

    for (const field of fields) {
      const value = this.readBooleanField(source, field);
      if (value === true) {
        return true;
      }
    }

    return false;
  }

  private readBooleanField(source: unknown, field: string): boolean | undefined {
    if (typeof source !== 'object' || source === null) {
      return undefined;
    }

    const value = Reflect.get(source, field);
    return typeof value === 'boolean' ? value : undefined;
  }
}
